import React, { useState, useEffect } from "react";
import {Create} from "@refinedev/mui";
import {
    Box,
    TextField,
    Alert,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Typography,
    Switch,
    Divider,
    Collapse,
    Stack,
    useTheme,
    Autocomplete,
} from "@mui/material";
import {Radio as RadioIcon} from "lucide-react";
import {useForm} from "@refinedev/react-hook-form";
import {IResourceComponentsProps, useTranslate, useNotification, useNavigation} from "@refinedev/core";
import { useAuth } from "../../contexts/auth-context";
import axiosInstance from "../../api/axios-config";

export const CompanyCreate: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const { user: currentUser } = useAuth();
    const { open } = useNotification();
    const { list } = useNavigation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orgType, setOrgType] = useState<'family' | 'enterprise'>('family');

    // Télésurveilleur state
    const [isTeleSurveilleur, setIsTeleSurveilleur] = useState(false);

    // Monitoring provider state
    const [monitoringProviders, setMonitoringProviders] = useState<Array<{ id: number; code: string; name: string }>>([]);
    const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);

    const isGuest = currentUser?.role === 'GUEST';
    const canEditTeleSurveilleur = currentUser?.role === 'ADMIN' || currentUser?.role === 'SAFEE_STAFF';

    // Fetch monitoring providers for the select dropdown
    useEffect(() => {
        if (!canEditTeleSurveilleur) return;
        axiosInstance.get("/monitoring/providers").then((res) => {
            if (res.data?.data) {
                setMonitoringProviders(res.data.data);
            }
        }).catch(() => {
            // Silently ignore — user may not have access
        });
    }, [canEditTeleSurveilleur]);

    const {
        saveButtonProps,
        refineCore: { formLoading },
        register,
        formState: { errors },
        handleSubmit,
    } = useForm();

    const onSubmit = async (data: any) => {
        setIsSubmitting(true);

        try {
            // Pour les GUEST, utiliser l'endpoint spécial qui crée et assigne
            if (isGuest) {
                const response = await axiosInstance.post('/company/create-and-assign', {
                    nom: data.nom,
                    limitUser: data.limitUser || (orgType === 'family' ? 10 : 1000)
                });

                console.log('Company created and user assigned:', response.data);

                open?.({
                    type: "success",
                    message: translate('company.create_success', 'Organisation créée avec succès !'),
                    description: translate('company.role_updated', 'Votre rôle a été mis à jour en Modérateur.'),
                });

                // Rafraîchir la page après un court délai pour recharger avec les nouveaux droits
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                // Pour les autres rôles, utiliser la création standard
                const payload: any = {
                    nom: data.nom,
                    limitUser: data.limitUser
                };

                // Ajouter les champs télésurveilleur si l'utilisateur peut les modifier
                if (canEditTeleSurveilleur) {
                    payload.isTeleSurveilleur = isTeleSurveilleur;
                    if (isTeleSurveilleur && selectedProviderId) {
                        payload.monitoringProviderId = selectedProviderId;
                    }
                }

                const response = await axiosInstance.post('/company', payload);

                open?.({
                    type: "success",
                    message: translate('company.create_success', 'Organisation créée avec succès !'),
                });

                list('company');
            }
        } catch (error: any) {
            console.error('Error creating company:', error);
            open?.({
                type: "error",
                message: translate('company.create_error', 'Erreur lors de la création'),
                description: error.response?.data?.message || error.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Create
            isLoading={formLoading || isSubmitting}
            saveButtonProps={{
                ...saveButtonProps,
                onClick: handleSubmit(onSubmit),
                disabled: isSubmitting
            }}
            title={isGuest ? translate('company.create_your_org', 'Créez votre organisation') : translate('company.create', 'Créer une entreprise')}
        >
            <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                autoComplete="off"
            >
                {isGuest && (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate('company.guest_create_info', 'Une fois votre organisation créée, vous deviendrez automatiquement Modérateur et pourrez inviter des membres.')}
                        </Alert>

                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                            <FormLabel component="legend">
                                {translate('company.org_type', 'Type d\'organisation')}
                            </FormLabel>
                            <RadioGroup
                                value={orgType}
                                onChange={(e) => setOrgType(e.target.value as 'family' | 'enterprise')}
                            >
                                <FormControlLabel
                                    value="family"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                {translate('company.type_family', 'Famille')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('company.type_family_desc', 'Jusqu\'à 10 membres')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="enterprise"
                                    control={<Radio />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                {translate('company.type_enterprise', 'Entreprise')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('company.type_enterprise_desc', 'Jusqu\'à 1000 membres')}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}

                <TextField
                    {...register("nom", {
                        required: translate('validation.required', 'Ce champ est requis'),
                    })}
                    error={!!(errors as any)?.nom}
                    helperText={(errors as any)?.nom?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label={translate("company.fields.name")}
                    name="nom"
                    placeholder={
                        isGuest
                            ? (orgType === 'family'
                                ? translate('company.name_placeholder_family', 'Ex: Famille Dupont')
                                : translate('company.name_placeholder_enterprise', 'Ex: ACME Corp'))
                            : translate('company.name_placeholder', 'Nom de l\'entreprise')
                    }
                />

                {!isGuest && (
                    <TextField
                        {...register("limitUser", {
                            required: translate('validation.required', 'Ce champ est requis'),
                            min: {
                                value: 1,
                                message: translate('validation.min_users', 'Minimum 1 utilisateur')
                            }
                        })}
                        error={!!(errors as any)?.limitUser}
                        helperText={(errors as any)?.limitUser?.message}
                        margin="normal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        type="number"
                        label={translate("company.fields.limit")}
                        name="limitUser"
                        defaultValue={100}
                    />
                )}

                {/* Télésurveilleur Section - ADMIN and SAFEE_STAFF only */}
                {canEditTeleSurveilleur && (
                    <>
                        <Divider sx={{ my: 3 }} />

                        <Box>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                <RadioIcon size={24} color={theme.palette.primary.main} />
                                <Typography variant="h6" fontWeight={600}>
                                    {translate("company.telesurveilleur.title", "Configuration Télésurveilleur")}
                                </Typography>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {translate(
                                    "company.telesurveilleur.description",
                                    "Activez le mode télésurveilleur pour configurer le format des messages d'alerte envoyés aux centres de télésurveillance."
                                )}
                            </Typography>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isTeleSurveilleur}
                                        onChange={(e) => setIsTeleSurveilleur(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={translate("company.telesurveilleur.enable", "Activer le mode télésurveilleur")}
                            />

                            <Collapse in={isTeleSurveilleur}>
                                <Box sx={{ mt: 2 }}>
                                    <Autocomplete
                                        options={monitoringProviders}
                                        getOptionLabel={(option) => `${option.code} — ${option.name}`}
                                        value={monitoringProviders.find((p) => p.id === selectedProviderId) || null}
                                        onChange={(_event, newValue) => {
                                            setSelectedProviderId(newValue ? newValue.id : null);
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={translate("company.monitoring.monitoringProviderId", "Provider de télésurveillance")}
                                                InputLabelProps={{ shrink: true }}
                                                placeholder={translate("company.monitoring.selectProvider", "Aucun (sélectionner un provider)")}
                                            />
                                        )}
                                    />
                                </Box>
                            </Collapse>
                        </Box>
                    </>
                )}
            </Box>
        </Create>
    );
};
